import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Header, Icon, Message, Segment } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import apis from "../../apis";
import AutomationOverview from "./components/AutomationOverview";
import AutomationDetail from "./components/AutomationDetail";
import RunDetail from "./components/RunDetail";
import "./AutomationMonitoring.styles.scss";

const POLL_INTERVAL_MS = 30 * 1000;
const ACTIVE_RUN_STATES = ["running", "stuck"];

function errorMessage(error, fallback) {
  return error.response?.data?.message || fallback;
}

function decodeRouteParam(value) {
  if (!value) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

const AutomationMonitoringPage = ({ match }) => {
  const history = useHistory();
  const { automationId } = match.params;
  const runId = decodeRouteParam(match.params.runId);
  const showingRun = Boolean(automationId && runId);
  const showingDetail = Boolean(automationId && !runId);
  const showingOverview = !automationId;
  const [automations, setAutomations] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [runDetail, setRunDetail] = useState(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState("");
  const runRequestGeneration = useRef(0);
  const overviewRequestGeneration = useRef(0);
  const overviewLoadingGeneration = useRef(0);
  const runState = runDetail?.run?.state;

  const loadOverview = useCallback(async (showLoading = true) => {
    const requestGeneration = ++overviewRequestGeneration.current;
    const loadingGeneration = showLoading ? ++overviewLoadingGeneration.current : null;
    const isCurrentRequest = () => requestGeneration === overviewRequestGeneration.current;
    if (showLoading) setOverviewLoading(true);
    try {
      const { data } = await apis.automationMonitoring.getAutomations();
      if (isCurrentRequest()) setAutomations(data.items || []);
      if (isCurrentRequest()) setOverviewError("");
    } catch (error) {
      if (isCurrentRequest()) setOverviewError(errorMessage(error, "Could not load automation health."));
    } finally {
      if (showLoading && isCurrentRequest() && loadingGeneration === overviewLoadingGeneration.current) {
        setOverviewLoading(false);
      }
    }
  }, []);

  const loadRun = useCallback(async (showLoading = true) => {
    const requestGeneration = ++runRequestGeneration.current;
    const isCurrentRequest = () => requestGeneration === runRequestGeneration.current;
    if (!showingRun) {
      if (isCurrentRequest()) setRunDetail(null);
      if (isCurrentRequest()) setRunError("");
      if (isCurrentRequest()) setRunLoading(false);
      return;
    }
    if (showLoading && isCurrentRequest()) setRunLoading(true);
    try {
      const { data } = await apis.automationMonitoring.getRun(automationId, runId);
      if (isCurrentRequest()) setRunDetail(data);
      if (isCurrentRequest()) setRunError("");
    } catch (error) {
      if (isCurrentRequest()) setRunError(errorMessage(error, "Could not load this run."));
    } finally {
      if (isCurrentRequest()) setRunLoading(false);
    }
  }, [automationId, runId, showingRun]);

  useEffect(() => {
    if (!showingOverview) return undefined;
    loadOverview();
    const interval = window.setInterval(() => loadOverview(false), POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      overviewRequestGeneration.current += 1;
    };
  }, [loadOverview, showingOverview]);

  useEffect(() => {
    if (showingRun) {
      setRunDetail(null);
      setRunError("");
      loadRun();
    } else {
      runRequestGeneration.current += 1;
      setRunDetail(null);
      setRunError("");
      setRunLoading(false);
    }
    return () => {
      runRequestGeneration.current += 1;
    };
  }, [loadRun, showingRun]);

  useEffect(() => {
    const runIsActive = ACTIVE_RUN_STATES.includes(runState);
    if (!showingRun || !runIsActive) return undefined;
    const interval = window.setInterval(() => loadRun(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [loadRun, runState, showingRun]);

  const openRun = useCallback((selectedAutomationId, selectedRunId) => {
    history.push(`/automation-monitoring/${selectedAutomationId}/runs/${encodeURIComponent(selectedRunId)}`);
  }, [history]);

  const openAutomation = useCallback((selectedAutomationId) => {
    history.push(`/automation-monitoring/${selectedAutomationId}`);
  }, [history]);

  if (showingOverview) {
    return (
      <AutomationOverview
        items={automations}
        loading={overviewLoading}
        error={overviewError}
        onRefresh={() => loadOverview()}
        onOpenAutomation={openAutomation}
        onOpenRun={openRun}
      />
    );
  }

  if (showingDetail) {
    return (
      <AutomationDetail
        key={automationId}
        automationId={automationId}
        onBack={() => history.push("/automation-monitoring")}
        onOpenRun={openRun}
      />
    );
  }

  return (
    <div className="automation-monitoring">
      <Segment className="automation-monitoring__header" inverted>
        <Header as="h1">Automation run</Header>
        <div>
          <Button onClick={() => history.push("/automation-monitoring")}>
            <Icon name="arrow left" />
            Back to overview
          </Button>
          <Button color="teal" loading={runLoading} onClick={() => loadRun()}>
            <Icon name="refresh" />
            Refresh
          </Button>
        </div>
      </Segment>

      {runError ? <Message negative content={runError} /> : null}
      {runLoading && !runDetail ? <Message content="Loading run..." /> : null}

      {runDetail?.run ? <RunDetail detail={runDetail} /> : null}
    </div>
  );
};

AutomationMonitoringPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      automationId: PropTypes.string,
      runId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default AutomationMonitoringPage;
